// Guardrail: Veera surfaces descriptive observations only.
// Do NOT add scoring, evaluation, or behavior direction here.
// Veera role: Augments recall and consistency; never judgment or instruction.
import { useEffect, useRef, useState } from "react";
import { History, Loader2, MoreHorizontal, Sparkles, X } from "lucide-react";
import { CoachVeeraComposer } from "@/components/chat/CoachVeeraComposer";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useCoachVeeraDictation } from "@/hooks/useCoachVeeraDictation";
import { useLeaderChat } from "@/hooks/useLeaderChat";
import { useUserSettings } from "@/hooks/useUserSettings";
import { ChatDeliveryRecovery } from "./ChatDeliveryRecovery";
import { ChatMessageBubble } from "./ChatMessageBubble";
import { ReflectionDuePrompt } from "./ReflectionDuePrompt";
import { useNextDueReflection, dismissReflectionPrompt } from "@/hooks/useDueReflections";

interface VeeraSidebarProps {
  pageContext?: {
    pageName: string;
    data?: Record<string, unknown>;
  };
  className?: string;
  fullWidth?: boolean;
  onClose?: () => void;
  contextLabel?: string;
  composerDisabled?: boolean;
  lockedMessage?: string;
  onViewPlans?: () => void;
  onOpenFullPage?: () => void;
  onOpenSettings?: () => void;
  surface?: "page" | "panel";
  onDraftStateChange?: (hasDraft: boolean) => void;
  isActive?: boolean;
}

const PAGE_SUGGESTIONS: Record<string, string[]> = {
  dashboard: [
    "What recent moments are worth revisiting?",
    "What themes are showing up lately?",
    "What feels important to explore next?",
  ],
  progress: [
    "What recent moments are worth revisiting?",
    "What observations stand out from recent moments?",
    "What feels important to explore next?",
  ],
  insights: [
    "Which recent moments connect to this theme?",
    "What observations support this theme?",
    "What feels important to explore next?",
  ],
  settings: [
    "How do I manage notifications and privacy?",
    "Where can I adjust reminders?",
    "How do I turn Coach Veera on or off?",
  ],
  synthesis: [
    "What recent moments are worth revisiting?",
    "What themes are showing up lately?",
    "What feels important to explore next?",
  ],
  messages: [
    "What recent moments are worth revisiting?",
    "What themes are showing up lately?",
    "What feels important to explore next?",
  ],
  default: [
    "What recent moments are worth revisiting?",
    "What themes are showing up lately?",
    "What feels important to explore next?",
  ],
};

export function VeeraSidebar({
  pageContext,
  className,
  fullWidth,
  onClose,
  contextLabel,
  composerDisabled = false,
  lockedMessage,
  onViewPlans,
  onOpenFullPage,
  onOpenSettings,
  surface = "page",
  onDraftStateChange,
  isActive = true,
}: VeeraSidebarProps) {
  const [inputValue, setInputValue] = useState("");
  const [promptDismissed, setPromptDismissed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastSubmittedMessageRef = useRef("");
  const errorAtSendRef = useRef<string | null>(null);
  const pendingSendRef = useRef(false);
  const errorClearedAfterSendRef = useRef(false);
  const observedBusyAfterSendRef = useRef(false);
  const dictationStopRequestedRef = useRef(false);
  const {
    messages,
    isLoading,
    sendMessage,
    messagesEndRef,
    isStreaming,
    streamingContent,
    error,
    canSend,
    deliveryRecovery,
  } = useLeaderChat(pageContext);
  const dictation = useCoachVeeraDictation({
    currentValue: inputValue,
    onValueChange: setInputValue,
  });
  const { status: dictationStatus, stop: stopDictation } = dictation;
  const { data: userSettings, isLoading: settingsLoading } = useUserSettings();
  const nextDueReflection = useNextDueReflection();

  const suggestions = PAGE_SUGGESTIONS[pageContext?.pageName || "default"] || PAGE_SUGGESTIONS.default;
  const requiresReconciliation = deliveryRecovery?.required || error === "stream_incomplete";
  const isBusy = isLoading || isStreaming || composerDisabled || requiresReconciliation || canSend === false;
  const companionDisabled = userSettings?.companionEnabled === false;
  const canFocusComposer = isActive && !settingsLoading && !companionDisabled && !isBusy;

  useEffect(() => {
    if (canFocusComposer) inputRef.current?.focus();
  }, [canFocusComposer]);

  useEffect(() => {
    const recordingMustStop = dictationStatus === "recording"
      && (!isActive || settingsLoading || companionDisabled || isBusy);

    if (recordingMustStop && !dictationStopRequestedRef.current) {
      dictationStopRequestedRef.current = true;
      void stopDictation();
      return;
    }

    if (dictationStatus !== "recording") dictationStopRequestedRef.current = false;
  }, [companionDisabled, dictationStatus, isActive, isBusy, settingsLoading, stopDictation]);

  useEffect(() => {
    onDraftStateChange?.(Boolean(inputValue.trim()));
  }, [inputValue, onDraftStateChange]);

  useEffect(() => {
    if (requiresReconciliation) {
      pendingSendRef.current = false;
      lastSubmittedMessageRef.current = "";
      return;
    }
    if (!pendingSendRef.current) return;

    if (isLoading || isStreaming) observedBusyAfterSendRef.current = true;

    if (!error) {
      errorClearedAfterSendRef.current = true;
      if (observedBusyAfterSendRef.current && !isLoading && !isStreaming) {
        pendingSendRef.current = false;
        lastSubmittedMessageRef.current = "";
      }
      return;
    }

    const isNewError = error !== errorAtSendRef.current || errorClearedAfterSendRef.current;
    if (isNewError && !inputValue && lastSubmittedMessageRef.current) {
      setInputValue(lastSubmittedMessageRef.current);
      pendingSendRef.current = false;
    }
  }, [error, inputValue, isLoading, isStreaming, requiresReconciliation]);

  const sendGuarded = (rawMessage: string) => {
    const message = rawMessage.trim();
    if (!message || isBusy) return;

    lastSubmittedMessageRef.current = message;
    errorAtSendRef.current = error;
    pendingSendRef.current = true;
    errorClearedAfterSendRef.current = !error;
    observedBusyAfterSendRef.current = false;
    setInputValue("");
    void sendMessage(message);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    sendGuarded(inputValue);
  };

  const handleDismissPrompt = () => {
    if (nextDueReflection) dismissReflectionPrompt(nextDueReflection.id);
    setPromptDismissed(true);
  };

  if (settingsLoading) {
    return (
      <div
        className={cn("relative flex h-full min-h-40 items-center justify-center bg-card", !fullWidth && "border-l border-border", className)}
        role="status"
        aria-label="Loading Coach Veera"
      >
        {onClose ? (
          <Button type="button" size="icon" variant="ghost" aria-label="Close Coach Veera" onClick={onClose} className="absolute right-3 top-3 h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        ) : null}
        <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
        <span className="sr-only">Loading Coach Veera</span>
      </div>
    );
  }

  if (companionDisabled) {
    return (
      <div
        data-testid="leader-veera-sidebar"
        className={cn("flex h-full flex-col bg-card", !fullWidth && "border-l border-border", className)}
      >
        {onClose ? (
          <div className="flex justify-end p-3 pb-0">
            <Button type="button" size="icon" variant="ghost" aria-label="Close Coach Veera" onClick={onClose} className="h-8 w-8">
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : null}
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-foreground">Coach Veera is turned off</h2>
            <p className="mt-1 text-sm text-muted-foreground">Turn Coach Veera on in Settings when you want to use it again.</p>
          </div>
          {onOpenSettings ? <Button type="button" variant="outline" onClick={onOpenSettings}>Open Settings</Button> : null}
        </div>
      </div>
    );
  }

  const showReflectionPrompt = messages.length === 0 && nextDueReflection && !promptDismissed;

  return (
    <div
      data-testid="leader-veera-sidebar"
      className={cn("flex h-full flex-col bg-card", !fullWidth && "border-l border-border", className)}
    >
      <div
        data-testid="leader-veera-sidebar-header"
        className={cn(
          "flex items-center justify-between border-b border-border px-4 py-3",
          surface === "panel" && "bg-primary text-primary-foreground md:bg-card md:text-foreground",
        )}
      >
        <div className="flex items-center gap-2">
          <div className={cn(
            "flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-violet-500 to-purple-600 text-white",
            surface === "panel" && "bg-none bg-white/15 md:bg-gradient-to-br",
          )}>
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <h2 className={cn(
              "text-sm font-semibold text-foreground",
              surface === "panel" && "text-primary-foreground md:text-foreground",
            )}>Coach Veera</h2>
            <p className={cn(
              "text-xs text-muted-foreground",
              surface === "panel" && "text-primary-foreground/75 md:text-muted-foreground",
            )}>Reflection grounded in your recent context</p>
            {contextLabel ? <p className={cn(
              "text-xs font-medium text-violet-700",
              surface === "panel" && "text-violet-100 md:text-violet-700",
            )}>{contextLabel}</p> : null}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {onOpenFullPage ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label="More Coach Veera options"
                  className={cn(
                    "h-8 w-8",
                    surface === "panel" && "text-primary-foreground hover:bg-white/10 hover:text-white md:text-foreground md:hover:bg-accent md:hover:text-accent-foreground",
                  )}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onOpenFullPage} className="gap-2">
                  <History className="h-4 w-4" />
                  Open conversation history
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
          {onClose ? (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              aria-label="Close Coach Veera"
              onClick={onClose}
              className={cn(
                "h-8 w-8",
                surface === "panel" && "text-primary-foreground hover:bg-white/10 hover:text-white md:text-foreground md:hover:bg-accent md:hover:text-accent-foreground",
              )}
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>

      <ScrollArea className="min-h-0 flex-1 px-4">
        <div className="space-y-4 py-4">
          {messages.length === 0 ? (
            <div className="space-y-3">
              {showReflectionPrompt ? (
                <ReflectionDuePrompt
                  reflection={nextDueReflection}
                  onDismiss={handleDismissPrompt}
                  showSeeAll
                  compact={false}
                />
              ) : null}

              <div className="py-3 text-center">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <h3 className="mb-0.5 text-sm font-medium text-foreground">Hi, I&apos;m Coach Veera</h3>
                <p className="text-xs text-muted-foreground">
                  Use Coach Veera to revisit recent moments, reflect on what matters, and prepare what you want to explore next.
                </p>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Quick questions</p>
                <div className="space-y-1.5">
                  {suggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => sendGuarded(suggestion)}
                      disabled={isBusy}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-left text-sm transition-colors hover:border-primary/30 hover:bg-accent disabled:opacity-50"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            messages.map((message) => (
              <div key={message.id} className={cn("flex", message.role === "user" ? "justify-end" : "justify-start")}>
                <div className="max-w-[90%]">
                  <ChatMessageBubble
                    content={message.content}
                    role={message.role}
                    className={message.role === "user" ? "rounded-xl rounded-br-sm" : "rounded-xl rounded-bl-sm"}
                  />
                </div>
              </div>
            ))
          )}

          {isStreaming ? (
            <div
              className="flex justify-start"
              role="status"
              aria-live="polite"
              aria-label="Coach Veera is responding"
            >
              <div className="max-w-[90%]">
                <ChatMessageBubble content={streamingContent} role="assistant" className="rounded-xl rounded-bl-sm" />
              </div>
            </div>
          ) : null}

          {isLoading && !isStreaming ? (
            <div
              className="flex justify-start"
              role="status"
              aria-live="polite"
              aria-label="Coach Veera is preparing a response"
            >
              <div className="rounded-xl bg-muted px-3 py-2">
                <div className="flex gap-1">
                  <span className="h-2 w-2 animate-bounce rounded-full bg-primary/40" style={{ animationDelay: "0ms" }} />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-primary/40" style={{ animationDelay: "150ms" }} />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-primary/40" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          ) : null}

          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      <div className="flex-shrink-0 border-t border-border bg-background p-3">
        {lockedMessage ? (
          <div className="mb-3 rounded-lg bg-violet-50 p-3 text-sm text-violet-950">
            <p>{lockedMessage}</p>
            {onViewPlans ? <Button type="button" variant="link" className="h-auto px-0 pt-2" onClick={onViewPlans}>View plans</Button> : null}
          </div>
        ) : null}
        <ChatDeliveryRecovery recovery={deliveryRecovery} />
        {error && !requiresReconciliation ? (
          <div className="mb-3 text-sm text-destructive" role="status" aria-live="polite">
            {error}{lastSubmittedMessageRef.current && inputValue === lastSubmittedMessageRef.current ? " Your message is ready to retry." : ""}
          </div>
        ) : null}
        <CoachVeeraComposer
          value={inputValue}
          onValueChange={setInputValue}
          onSubmit={handleSubmit}
          placeholder="Message Coach Veera..."
          disabled={isBusy}
          submitDisabled={!inputValue.trim() || isBusy}
          inputRef={inputRef}
          controlsClassName={cn("items-center", surface === "panel" && "pb-[env(safe-area-inset-bottom)]")}
          inputClassName="h-10 rounded-xl text-sm"
          micButtonClassName="h-10 w-10"
          sendButtonClassName="h-10 w-10 bg-violet-600 hover:bg-violet-700"
          dictation={dictation}
        />
      </div>
    </div>
  );
}
